import { useGetProfileDetailsQuery, useGetProfileImageQuery } from '@/app/(protected)/profile/api/ProfileApi';
import ProfileImage from '@/components/ProfileImage';
import useUser from '@auth/useUser';
import FuseSvgIcon from '@fuse/core/FuseSvgIcon';
import { Button, ListItemIcon, ListItemText, MenuItem, Typography } from '@mui/material';
import Popover, { PopoverProps } from '@mui/material/Popover/Popover';
import { twMerge } from 'tailwind-merge';
import { useState } from 'react';
import { Link } from 'react-router';


type UserMenuProps = {
	className?: string;
	popoverProps?: Partial<PopoverProps>;
	arrowIcon?: string;
};

/**
 * The user menu.
 */
function UserMenu(props: UserMenuProps) {
	const { className, popoverProps } = props;
	const { data: user, signOut } = useUser();
	const { data } = useGetProfileDetailsQuery(undefined, { skip: !user });
	const { data: image } = useGetProfileImageQuery(undefined, { skip: !user });
	const [userMenu, setUserMenu] = useState<HTMLElement | null>(null);

	const userMenuClick = (event: React.MouseEvent<HTMLElement>) => {
		setUserMenu(event.currentTarget);
	};

	const userMenuClose = () => {
		setUserMenu(null);
	};

	if (!user) {
		return null;
	}

	return (
		<>
			<Button
				className={twMerge(
					'user-menu flex justify-start shrink-0  min-h-56 h-56 rounded-lg p-8 space-x-12',
					className
				)}
				sx={() => ({
					borderColor: 'var(--navbar-border)',
					'&:hover, &:focus': {
						backgroundColor: 'var(--user-menu-hover-bg)'
					}
				})}
				onClick={userMenuClick}
				color="inherit"
			>
				<ProfileImage photo={image} user={user} />

				<div className="flex flex-col flex-auto space-y-8">
					<Typography
						component="span"
						className="title flex font-semibold text-base capitalize truncate  tracking-tight leading-none"
						sx={{ color: 'var(--user-menu-title)' }}
					>
						{data?.first_name} {data?.last_name}
					</Typography>
					<Typography
						className="subtitle flex text-md font-medium tracking-tighter leading-none"
						sx={{ color: 'var(--user-menu-subtitle)' }}
					>
						{user?.email}
					</Typography>
				</div>
				<div className="flex flex-shrink-0 items-center space-x-8">
					<FuseSvgIcon
						className="arrow"
						size={13}
					>
						{userMenu ? 'heroicons-outline:chevron-down' : 'heroicons-outline:chevron-up'}
					</FuseSvgIcon>
				</div>
			</Button>
			<Popover
				open={Boolean(userMenu)}
				anchorEl={userMenu}
				onClose={userMenuClose}
				anchorOrigin={{
					vertical: 'top',
					horizontal: 'center'
				}}
				transformOrigin={{
					vertical: 'bottom',
					horizontal: 'center'
				}}
				classes={{
					paper: 'py-8 min-w-256'
				}}
				{...popoverProps}
			>
				<MenuItem
					component={Link}
					to="/profile"
					onClick={userMenuClose}
				>
					<ListItemIcon className="min-w-36">
						<FuseSvgIcon>heroicons-outline:user</FuseSvgIcon>
					</ListItemIcon>
					<ListItemText
						color="textPrimary"
						primary="My Profile"
					/>
				</MenuItem>
				<MenuItem
					onClick={() => {
						signOut();
					}}
				>
					<ListItemIcon className="min-w-36">
						<FuseSvgIcon>heroicons-outline:arrow-right-on-rectangle</FuseSvgIcon>
					</ListItemIcon>
					<ListItemText primary="Sign out" />
				</MenuItem>
			</Popover>
		</>
	);
}

export default UserMenu;
